import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { applyContainerQuery } from 'react-container-query';
import Markdown from 'react-markdown';

import Button from '../Button';
import containerQuery from '../../constants/containerQuery';
import containerQueryProps from '../../props/containerQueryProps';
import EventBookingContainer from '../../containers/EventBookingContainer';
import EventShape from '../types/eventShape';
import eventTypes from '../../constants/eventTypes';
import Modal from '../Modal';
import SafetyMeasures from '../SafetyMeasures';
import SafetyMeasureList from '../SafetyMeasureList';
import TextBlurb from '../TextBlurb';
import {
  isInFuture,
} from '../../utils/resyCoreUtils';
import { openExternalUrl } from '../../utils';
import Close from '../../../../consumer_web/app/modules/svg/dynamic/close.svg';

import './EventDetailsContent.scss';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import Link from '../Link';

const { GENERAL_ADMISSION, SEATED } = eventTypes;

const hasActiveCategories = (categories = []) => categories.some((category) => category.items.length > 0);

const EventDetailsContent = ({
  $rootScope,
  Analytics,
  containerQuery: {
    md, lg, xl, xxl,
  },
  city,
  context,
  copyPosition,
  date,
  event,
  eventSlug,
  fetchEventDetails,
  login,
  needToKnowCopy,
  notifyService,
  onTicketQuantitySelect,
  enableBookNow,
  previewMode,
  safetyMeasures: categories,
  safetyMeasureUrl,
  seats,
  selectResy,
  showAbout,
  showNotify,
  ticketTypes,
  type,
  userIsGda,
  venue,
}) => {
  const {
    aboutCopy,
    description,
    generalSaleDate,
    packages,
  } = event;

  const eventLargePartyMessage = packages?.[0]?.party?.large?.message?.[1] ?? null;

  const {
    id: {
      resy: venueId,
    } = {},
    url_slug: venueSlug,
  } = venue;

  const [isExternalLinkModalOpen, setIsExternalLinkModalOpen] = useState(false);
  const [externalLinkHref, setExternalLinkHref] = useState(undefined);

  const handleMarkdownAnchor = (href) => function () {
    setExternalLinkHref(href);
    setIsExternalLinkModalOpen(true);
  };

  const markdownCustomComponents = {
    a: ({ href, children }) => <Link onClick={handleMarkdownAnchor(href)}>{children[0]}</Link>,
  };

  return (
    <div className="EventDetailsContent">
      {isExternalLinkModalOpen && (
        <Modal
          isOpen
          shouldCloseOnEsc
          shouldCloseOnOverlayClick
          onRequestClose={() => setIsExternalLinkModalOpen(false)}
          hasFooter
          hasSecondaryCTA
          primaryCTAText={getTranslation({ translationKey: 'onboarding.continue', fallback: 'Continue' })}
          primaryCTAVariant="primary"
          primaryCTAAdditionalProps={{
            onClick: () => openExternalUrl(externalLinkHref),
            'data-test-id': 'event-details-external-link-primary-cta',
          }}
          secondaryCTAAdditionalProps={{
            onClick: () => setIsExternalLinkModalOpen(false),
            'data-test-id': 'event-details-external-link-secondary-cta',
          }}
          secondaryCTAText={getTranslation({ translationKey: 'invite_decline_confirm_no', fallback: 'Nevermind' })}
          secondaryCTAVariant="secondary"
          styles={{
            content: {
              maxWidth: '500px',
              inset: '35vh 0px auto',
            },
          }}
        >
          <div className="EventDetailsContent__modal__header">
            <Button
              onClick={() => setIsExternalLinkModalOpen(false)}
              variant="no-style"
              className="Button__close"
              aria-label={getTranslation({
                translationKey: 'close',
                fallback: 'Close',
              })}
              data-test-id="event-details-external-link-button-close"
            >
              <Close />
            </Button>
          </div>
          <TranslationProvider translationKey="warning_leaving_resy">
            <div className="EventDetailsContent__modal__body">
              You are about to leave Resy&apos;s website.
            </div>
          </TranslationProvider>
        </Modal>
      )}
      <div className={cx('EventDetailsContent__container', {
        'EventDetailsContent__container--lg': lg,
        'EventDetailsContent__container--md': md,
        'EventDetailsContent__container--xl': xl,
        'EventDetailsContent__container--xxl': xxl,
        'EventDetailsContent__container--copyOnBottom': copyPosition === 'bottom',
      })}
      >
        <EventBookingContainer
          $rootScope={$rootScope}
          Analytics={Analytics}
          ccRequired={venue.config?.allow_bypass_payment_method !== 1}
          city={city}
          context={context}
          date={date}
          event={event}
          eventLargePartyMessage={eventLargePartyMessage}
          eventSlug={eventSlug}
          fetchEventDetails={fetchEventDetails}
          login={login}
          notifyService={notifyService}
          onTicketQuantitySelect={onTicketQuantitySelect}
          enableBookNow={enableBookNow}
          previewMode={previewMode}
          seats={seats}
          selectResy={selectResy}
          showNotify={showNotify}
          taxIncluded={venue.tax_included}
          ticketTypes={ticketTypes}
          type={type}
          userIsGda={userIsGda}
          venueId={venueId}
          venueIsGda={venue.is_global_dining_access}
          venueSlug={venueSlug}
        />
        <div className="EventDetailsContent__blurbs">
          {!!description && isInFuture(generalSaleDate) && (
            <div className="EventDetailsContent__desc">
              {description}
            </div>
          )}

          {!!needToKnowCopy && (
          <TextBlurb
            className="TextBlurb__need-to-know"
            copy={{
              title: {
                translationKey: 'venue_need_to_know_header',
                value: 'Need to Know',
              },
              body: {
                value: <Markdown components={markdownCustomComponents}>{needToKnowCopy}</Markdown>,
              },
            }}
          />
          )}
          {showAbout && (
          <TextBlurb
            className="TextBlurb__about"
            copy={{
              title: {
                translationKey: 'about',
                value: 'About',
              },
              body: {
                value: <Markdown components={markdownCustomComponents}>{aboutCopy}</Markdown>,
              },
            }}
          />
          )}
          {hasActiveCategories(categories) && (context === 'widget')
          && (
          <SafetyMeasureList
            categories={categories}
            className="SafetyMeasureList__widget"
            url={safetyMeasureUrl}
            venueName={venue.name}
            referrer="event page"
          />
          )}
          {hasActiveCategories(categories) && (context !== 'widget')
          && (
            <SafetyMeasures
              categories={categories}
              url={safetyMeasureUrl}
              venueName={venue.name}
              isMobile={!md}
              referrer="event page"
            />
          )}
        </div>
      </div>
    </div>
  );
};

EventDetailsContent.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  city: PropTypes.string,
  containerQuery: containerQueryProps,
  context: PropTypes.string,
  copyPosition: PropTypes.oneOf(['top', 'bottom']),
  date: PropTypes.string.isRequired,
  event: EventShape.isRequired,
  eventSlug: PropTypes.string,
  fetchEventDetails: PropTypes.func.isRequired,
  login: PropTypes.func,
  needToKnowCopy: PropTypes.string,
  notifyService: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onTicketQuantitySelect: PropTypes.func.isRequired,
  enableBookNow: PropTypes.bool.isRequired,
  previewMode: PropTypes.bool,
  safetyMeasures: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      active: PropTypes.bool.isRequired,
      id: PropTypes.number.isRequired,
      order_num: PropTypes.number.isRequired,
      safety_category_id: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    })),
    order_num: PropTypes.number.isRequired,
    value: PropTypes.string.isRequired,
  })),
  safetyMeasureUrl: PropTypes.string,
  seats: PropTypes.number,
  selectResy: PropTypes.func.isRequired,
  showAbout: PropTypes.bool,
  showNotify: PropTypes.bool.isRequired,
  ticketTypes: PropTypes.arrayOf(PropTypes.shape({
    availableQuantity: PropTypes.number,
    description: PropTypes.string,
    isGda: PropTypes.bool,
    isPresaleSoldOut: PropTypes.bool,
    price: PropTypes.string,
    priceNumber: PropTypes.number,
    slots: PropTypes.arrayOf(PropTypes.shape({})),
    soldOut: PropTypes.bool,
    title: PropTypes.string,
  })),
  type: PropTypes.oneOf([GENERAL_ADMISSION, SEATED]),
  userIsGda: PropTypes.bool,
  venue: PropTypes.shape({
    config: PropTypes.shape({
      allow_bypass_payment_method: PropTypes.number,
    }),
    id: PropTypes.shape({
      resy: PropTypes.number,
    }),
    is_global_dining_access: PropTypes.bool,
    large_party_message: PropTypes.string,
    locality: PropTypes.string,
    location: PropTypes.shape({
      code: PropTypes.string,
    }),
    name: PropTypes.string.isRequired,
    neighborhood: PropTypes.string,
    tax_included: PropTypes.bool,
    url_slug: PropTypes.string,
  }).isRequired,
};

EventDetailsContent.defaultProps = {
  city: undefined,
  containerQuery: {},
  context: undefined,
  copyPosition: 'top',
  eventSlug: undefined,
  login: null,
  needToKnowCopy: '',
  notifyService: undefined,
  previewMode: false,
  safetyMeasures: [],
  safetyMeasureUrl: undefined,
  seats: undefined,
  showAbout: true,
  ticketTypes: [],
  type: undefined,
  userIsGda: false,
};

EventDetailsContent.displayName = 'EventDetailsContent';
export { EventDetailsContent as Component };
export default applyContainerQuery(EventDetailsContent, containerQuery);
